'use client'
import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { CONFIG_ENABLE_ZIP } from '@src/config/env'
import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import { IZipCodeInfo } from '@src/server-side/shared-server-side.types'
import { getCookieStorage } from '@src/utils/enhancedCookie'
import { getCountryPrefixedCookieKey } from '@src/utils/i18n.utils'

import zipCodeService from '../../../services/zipCodeService'
import AuthUtil from '../../../utils/authUtil'
import isBrowser from '../../../utils/isBrowser'
import { EXCLUDE_ZIP_CODE_FETCH_FOR_PAGE_TYPE_LIST } from '../constants'
import {
  getZipCodeModalInitialState,
  validateZipCode,
  setZipCodeCookies,
  getZipCodeFromCookie,
  isValidZipCodeForLocale,
} from '../utils/zipcode.utils'

import ZipCodeContext from './zipcode.context'

// @i18n-ignore
export const ZIP_COOKIE_KEY = 'zip-code'
// @i18n-ignore
export const USER_ENTERED_ZIP_COOKIE_KEY = 'user-enter-zip-code'
export const IP_ADDRESS_COOKIE_KEY = 'ip-address'
export const NEW_IP_ADDRESS_COOKIE_KEY = 'new-ip-address'

export const ZIP_QUERY_PARAMS_KEYS = {
  ZIP_CODE: 'zip-code',
  USER_ENTERED_ZIP_CODE: 'user-enter-zip-code',
  IP_ADDRESS: 'ip-address',
  NEW_IP_ADDRESS: 'new-ip-address',
}

const ZipCodeContextProvider: FC<PropsWithChildren<{ zipCodeInfo?: IZipCodeInfo; pageType?: string }>> = ({
  zipCodeInfo,
  pageType,
  children,
}) => {
  const { isB2BMode = false } = useStorefrontModeContext()
  const { locale } = useLocaleContext()
  const localeConfig = useStatsigDynamicLocaleConfig()
  const [zipCode, setZipCode] = useState<string | null | undefined>(getZipCodeFromCookie(false, localeConfig))
  const [isZipcodeModalOpen, openZipcodeModal] = useState(getZipCodeModalInitialState)
  const isLoggedIn = AuthUtil.isLoggedIn()

  const updateZipCode = useCallback(
    (zip: string, isFromAPI: boolean) => {
      if (!CONFIG_ENABLE_ZIP || !isBrowser()) return
      if (isBrowser()) {
        const cookieStorage = getCookieStorage()
        const countryZipKey = getCountryPrefixedCookieKey(ZIP_COOKIE_KEY, locale)
        cookieStorage.setCookieLocalized(countryZipKey, zip, { isGlobal: true })
        // Only remove non-prefixed cookie if it exists and is valid for current locale
        // This prevents removing cookies from other countries when switching locales
        const nonPrefixedZip = cookieStorage.getCookieLocalized(ZIP_COOKIE_KEY, { isGlobal: true })
        if (nonPrefixedZip && isValidZipCodeForLocale(nonPrefixedZip, locale, localeConfig)) {
          cookieStorage.removeCookieLocalized(ZIP_COOKIE_KEY, { isGlobal: true })
        }

        if (!isFromAPI) {
          const countryUserKey = getCountryPrefixedCookieKey(USER_ENTERED_ZIP_COOKIE_KEY, locale)
          cookieStorage.setCookieLocalized(countryUserKey, zip, { isGlobal: true })
          // Only remove non-prefixed cookie if it exists and is valid for current locale
          // This prevents removing cookies from other countries when switching locales
          const nonPrefixedUserZip = cookieStorage.getCookieLocalized(USER_ENTERED_ZIP_COOKIE_KEY, { isGlobal: true })
          if (nonPrefixedUserZip && isValidZipCodeForLocale(nonPrefixedUserZip, locale, localeConfig)) {
            cookieStorage.removeCookieLocalized(USER_ENTERED_ZIP_COOKIE_KEY, { isGlobal: true })
          }
        }
      }
      setZipCode(zip)
    },
    [locale, localeConfig],
  )

  const validateAndUpdateZipCode = useCallback(
    (zip: string | undefined | null, isFromAPI = false) => {
      if (!CONFIG_ENABLE_ZIP) return false
      const isValidZip = validateZipCode(zip, localeConfig)
      if (zip && isValidZip) {
        updateZipCode(zip, isFromAPI)
        return true
      }
      return false
    },
    [updateZipCode, localeConfig],
  )

  const fetchZipCode = useCallback(async () => {
    if (!CONFIG_ENABLE_ZIP) return
    if (isB2BMode) return
    try {
      const zip = await zipCodeService.getZipCodeForUser()
      validateAndUpdateZipCode(zip, true)
    } catch (error) {
      console.error('Error occured during fetching zipcode ', error)
    }
  }, [validateAndUpdateZipCode, isB2BMode])

  useEffect(() => {
    setZipCodeCookies(zipCodeInfo ?? null, localeConfig)
    // this is done because for collection pages we are anyways fetching the zip code from the server side
    if (pageType && EXCLUDE_ZIP_CODE_FETCH_FOR_PAGE_TYPE_LIST.includes(pageType)) {
      return
    }
    if (!getZipCodeFromCookie(true, localeConfig)) {
      // Only check user-entered zip code, not zipCode from API
      // If no user-entered zip code exists, fetch from API
      fetchZipCode().catch((e: unknown) => {
        console.error(e)
      })
    }
  }, [fetchZipCode, isLoggedIn, zipCodeInfo, pageType, localeConfig])

  const handleModalOpen = useCallback((val: boolean) => {
    if (!CONFIG_ENABLE_ZIP) return
    const OVER_LAY_OPEN_CLASS = 'overlay-open'

    if (val) {
      window.document.body.classList.add(OVER_LAY_OPEN_CLASS)
    } else {
      window.document.body.classList.remove(OVER_LAY_OPEN_CLASS)
    }
    openZipcodeModal(val)
  }, [])

  return (
    <ZipCodeContext.Provider
      value={{
        zipCode,
        updateZipCode: validateAndUpdateZipCode,
        isZipcodeModalOpen,
        openZipcodeModal: handleModalOpen,
      }}
    >
      {children}
    </ZipCodeContext.Provider>
  )
}

export default ZipCodeContextProvider
